// src/components/admin/services/AdminServiceEditForm.jsx

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import fetchData from '../../../api/fetchData'; 
import { supportedLngs } from '../../../i18n';
import { useTranslation } from 'react-i18next';

function AdminServiceEditForm({ onContentUpdated }) {
  const { serviceId } = useParams(); 
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const isEditMode = !!serviceId;
  const [serviceItem, setServiceItem] = useState(null); 
  const [iconClass, setIconClass] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [editingLanguage, setEditingLanguage] = useState(i18n.language.split('-')[0] || 'ru');
  const [currentTitle, setCurrentTitle] = useState('');
  const [currentDescription, setCurrentDescription] = useState('');
  const [currentListItem, setCurrentListItem] = useState('');
  const [loading, setLoading] = useState(isEditMode);
  const [error, setError] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState({ message: '', type: '' });

  useEffect(() => {
    if (isEditMode) {
      const loadServiceData = async () => {
        setLoading(true);
        setError(null);
        try {
       
          const response = await fetchData(`/admin/services/admin_get_service.php?id=${serviceId}`, 'GET', null, { credentials: 'include' });
          if (response && response.status === 'success' && response.item) {
            const fetchedItem = response.item;
            setServiceItem(fetchedItem);
            setIconClass(fetchedItem.icon_class || '');
            setImageUrl(fetchedItem.image_url || '');
          } else {
            setError(response?.message || 'Не удалось загрузить данные услуги.');
          }
        } catch (err) {
          setError(err.message);
        } finally {
          setLoading(false);
        }
      };
      loadServiceData();
    } else { 
      setLoading(false);
    }
  }, [serviceId, isEditMode]);

 
  useEffect(() => {
    if (serviceItem && serviceItem.translations) {
      const langData = serviceItem.translations[editingLanguage] || { title: '', description: '', list_item: '' };
      setCurrentTitle(langData.title || '');
      setCurrentDescription(langData.description || '');
      setCurrentListItem(langData.list_item || '');
    } else if (!isEditMode) {
      setCurrentTitle('');
      setCurrentDescription('');
      setCurrentListItem('');
    }
  }, [editingLanguage, serviceItem, isEditMode]);

  const handleLanguageTabClick = (langCode) => {
    setEditingLanguage(langCode);
  };

const handleSave = async (e) => {
  e.preventDefault();
  setIsSaving(true);
  setSaveStatus({ message: '', type: '' }); 

  const payload = {
    serviceId: isEditMode ? serviceId : null,
    language_code: editingLanguage,
    title: currentTitle,
    description: currentDescription,
    list_item: currentListItem,
    icon_class: iconClass,
    image_url: imageUrl
  };

  try {
    const response = await fetchData(
      '/admin/services/admin_save_service.php', 
      'POST',
      payload,
      { credentials: 'include' }
    );

    if (response && response.status === 'success') {
      const messageToShow = t(response.messageKey, response.details);
      setSaveStatus({ message: messageToShow, type: 'success' });
      
      if (onContentUpdated) {
        onContentUpdated();
      }

   
      if (!isEditMode && response.newItemId) {
        setTimeout(() => {
          navigate(`/admin/services/edit/${response.newItemId}`);
        }, 1500);
      }
    } else {
      const errorDetails = response?.message || 'Unknown error';
      setSaveStatus({ 
        message: t('admin.services.saveErrorGeneric', { details: errorDetails }), 
        type: 'error' 
      });
    }
  } catch (err) {
    setSaveStatus({ 
      message: t('messages.error.generic', { details: err.message }), 
      type: 'error' 
    });
  } finally {
    setIsSaving(false);
    setTimeout(() => setSaveStatus({ message: '', type: '' }), 5000);
  }
};

  if (loading) return <p>{t('messages.loading')}</p>;
  if (error) return <p>{t('messages.error.generic', { details: error })}</p>;
  if (isEditMode && !loading && !serviceItem) return <p>{t('admin.services.itemNotFound', 'Услуга не найдена.')}</p>;

 

  return (
    <div className="admin-service-edit-form">
      <h3>
        {isEditMode 
          ? t('admin.services.editFormTitle', { id: serviceId }) 
          : t('admin.services.addFormTitle', 'Добавить новую услугу')}
      </h3>
      
      <div className="language-tabs">
{supportedLngs.map(langCode => (
        <button
          key={langCode}
          type="button"
          onClick={() => handleLanguageTabClick(langCode)}
          disabled={editingLanguage === langCode}
          className={editingLanguage === langCode ? 'active' : ''}
        >
          {langCode.toUpperCase()}
        </button>
      ))}
      </div>
      
      <form onSubmit={handleSave}>
        <fieldset>
          <legend>{t('admin.services.commonDataLegend', 'Общие данные')}</legend>
          <div>
            <label htmlFor="iconClass">{t('admin.services.iconClassLabel', 'Класс иконки (напр., fas fa-code)')}:</label>
            <input type="text" id="iconClass" value={iconClass} onChange={e => setIconClass(e.target.value)} />
          </div>
          <div>
            <label htmlFor="imageUrl">{t('admin.services.imageUrlLabel', 'URL изображения')}:</label>
            <input type="text" id="imageUrl" value={imageUrl} onChange={e => setImageUrl(e.target.value)} />
          </div>
        </fieldset>

        <fieldset>
          <legend>{t('admin.services.translatableDataLegend', 'Переводимые данные для языка:')} {editingLanguage.toUpperCase()}</legend>
          <div>
            <label htmlFor="currentTitle">{t('admin.services.titleLabel', 'Название услуги')}:</label>
            <input type="text" id="currentTitle" value={currentTitle} onChange={e => setCurrentTitle(e.target.value)} />
          </div>
          <div>
            <label htmlFor="currentDescription">{t('admin.services.descriptionLabel', 'Описание')}:</label>
            <textarea id="currentDescription" value={currentDescription} onChange={e => setCurrentDescription(e.target.value)} rows="5" />
          </div>
          <div>
            <label htmlFor="currentListItem">{t('admin.services.listItemLabel', 'Список особенностей (через ;)')}:</label>
            <textarea id="currentListItem" value={currentListItem} onChange={e => setCurrentListItem(e.target.value)} rows="3" />
          </div>
        </fieldset>
        
        <button type="submit" disabled={isSaving} style={{ marginTop: '10px' }}>
          {isSaving ? t('admin.buttons.saving') : `${t('admin.buttons.save')} ${editingLanguage.toUpperCase()}`}
        </button>
      </form>

      {saveStatus.message && (
        <p className={`form-status-message ${saveStatus.type}`}>
          {saveStatus.message}
        </p>
      )}
    </div>
  );
}

export default AdminServiceEditForm;